import React, { useEffect, useRef, useState } from "react";
import { backend_url } from "../App";
import { useNavigate, useLocation, Link } from "react-router-dom";
import {
  FaCheckCircle, FaBoxOpen, FaShippingFast, FaClipboardCheck,
  FaUser, FaMapMarkerAlt, FaPhone, FaEnvelope, FaVenusMars,
  FaCity, FaGlobe, FaEdit, FaSave, FaSignOutAlt, FaHistory,
  FaShoppingBag, FaCheck, FaTimesCircle, FaRegClock, FaWhatsapp,
} from "react-icons/fa";
import { MdDashboard, MdLocationOn } from "react-icons/md";
import { toast } from "react-toastify";

const indianStates = [
  "Andhra Pradesh","Arunachal Pradesh","Assam","Bihar","Chhattisgarh","Goa",
  "Gujarat","Haryana","Himachal Pradesh","Jharkhand","Karnataka","Kerala",
  "Madhya Pradesh","Maharashtra","Manipur","Meghalaya","Mizoram","Nagaland",
  "Odisha","Punjab","Rajasthan","Sikkim","Tamil Nadu","Telangana","Tripura",
  "Uttar Pradesh","Uttarakhand","West Bengal",
];

const statusIcons = {
  Confirmed: FaClipboardCheck,
  Packed: FaBoxOpen,
  Shipped: FaShippingFast,
  Delivered: FaCheckCircle,
};

// The linear fulfilment flow shown in the tracker.
const STATUS_FLOW = ["Confirmed", "Packed", "Shipped", "Delivered"];
// Map every backend status onto a step index (a freshly placed/paid order is "Confirmed").
const STATUS_INDEX = { Pending: 0, Confirmed: 0, Packed: 1, Shipped: 2, Delivered: 3 };
// Short description shown under each step in the vertical timeline.
const STEP_DESC = {
  Confirmed: "Your order has been placed and confirmed.",
  Packed: "We're preparing your item for dispatch.",
  Shipped: "Your order is on its way.",
  Delivered: "Delivered to your address.",
};

const UserDetails = () => {
  const location = useLocation();
  const [user, setUser] = useState({});
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const queryParams = new URLSearchParams(location.search);
  const [activeTab, setActiveTab] = useState(queryParams.get("tab") || "profile");
  const navigate = useNavigate();

  // Single fetch — reads userId from API response directly (no stale closure)
  useEffect(() => {
    const fetchAll = async () => {
      const token = localStorage.getItem("auth-token");
      if (!token) { navigate("/login"); return; }
      try {
        const res = await fetch(`${backend_url}/getuserdetails`, {
          headers: { "auth-token": token },
        });
        const data = await res.json();
        if (data.success) setUser(data.user);
      } catch (err) {
        console.error("Error fetching user details:", err);
      }
    };
    fetchAll();
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem("auth-token");
    navigate("/login");
  };

  const mainRef = useRef(null);
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    navigate(`/user-profile?tab=${tab}`, { replace: true });
    // On phones the menu sits above the content — bring the chosen section into view
    if (window.innerWidth < 768) {
      setTimeout(() => {
        const top = (mainRef.current?.getBoundingClientRect().top || 0) + window.scrollY - 80;
        window.scrollTo({ top, behavior: "smooth" });
      }, 60);
    }
  };

  // Profile completion
  const profileFields = ["name","email","phoneNumber","gender","address","city","pincode","country"];
  const filled = profileFields.filter((f) => user[f] && user[f] !== "").length;
  const completion = Math.round((filled / profileFields.length) * 100);

  // Initials avatar
  const initials = (user.name || "U").split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase();

  const sidebarItems = [
    { id: "profile", label: "Profile Details", icon: <FaUser /> },
    { id: "orders", label: "Order History", icon: <FaHistory /> },
    { id: "myOrders", label: "My Orders", icon: <FaShoppingBag /> },
  ];

  return (
    <div className="min-h-screen" style={{ background: "linear-gradient(180deg,var(--brand-primary-50) 0%,#f9fafb 100%)" }}>
      <div className="max-w-7xl mx-auto px-4 py-8 flex flex-col md:flex-row gap-6">

        {/* ── Sidebar ── */}
        <aside className="w-full md:w-72 flex-shrink-0">
          {/* User card */}
          <div className="rounded-2xl overflow-hidden shadow-md mb-4" style={{ background: "linear-gradient(135deg,var(--brand-primary-900),var(--brand-primary-500))" }}>
            <div className="px-6 pt-6 pb-4 flex flex-col items-center text-center">
              <div className="w-16 h-16 rounded-full flex items-center justify-center text-2xl font-black text-white shadow-lg mb-3"
                style={{ background: "linear-gradient(135deg,var(--brand-primary-400),var(--brand-primary-700))" }}>
                {initials}
              </div>
              <p className="text-white font-bold text-lg leading-tight">{user.name || "User"}</p>
              <p className="text-[var(--brand-primary-200)] text-xs mt-0.5 truncate max-w-full">{user.email || ""}</p>
              {/* Completion bar */}
              <div className="w-full mt-4">
                <div className="flex justify-between text-[11px] text-[var(--brand-primary-200)] mb-1">
                  <span>Profile completion</span>
                  <span className="font-bold">{completion}%</span>
                </div>
                <div className="w-full h-1.5 bg-white/20 rounded-full overflow-hidden">
                  <div className="h-full rounded-full transition-all duration-700"
                    style={{ width: `${completion}%`, background: "linear-gradient(90deg,var(--brand-primary-400),var(--brand-primary-700))" }} />
                </div>
              </div>
            </div>
          </div>

          {/* Nav items */}
          <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
            {sidebarItems.map((item) => (
              <button key={item.id} onClick={() => handleTabChange(item.id)}
                className={`w-full flex items-center gap-3 px-5 py-3.5 text-sm font-semibold transition-all duration-200 border-b border-gray-50 last:border-b-0
                  ${activeTab === item.id
                    ? "text-white"
                    : "text-gray-600 hover:text-[var(--brand-primary-700)] hover:bg-[var(--brand-primary-50)]"}`}
                style={activeTab === item.id
                  ? { background: "linear-gradient(90deg,var(--brand-primary-500),var(--brand-primary-400))" }
                  : {}}
              >
                <span className={activeTab === item.id ? "text-white" : "text-[var(--brand-primary-400)]"}>{item.icon}</span>
                {item.label}
              </button>
            ))}
            <button onClick={handleLogout}
              className="w-full flex items-center gap-3 px-5 py-3.5 text-sm font-semibold text-red-500 hover:bg-red-50 transition-all duration-200">
              <FaSignOutAlt /> Logout
            </button>
          </div>
        </aside>

        {/* ── Main ── */}
        <main ref={mainRef} className="flex-1 min-w-0">
          {activeTab === "profile" && (
            <ProfileSection user={user} setUser={setUser} editMode={editMode}
              setEditMode={setEditMode} saving={saving} setSaving={setSaving}
              errors={errors} setErrors={setErrors} completion={completion} />
          )}
          {activeTab === "orders" && <TrackOrderSection />}
          {activeTab === "myOrders" && <OrdersTable user={user} backend_url={backend_url} />}
        </main>
      </div>
    </div>
  );
};

// ──────────────────────── PROFILE SECTION ────────────────────────
const ProfileSection = ({ user, setUser, editMode, setEditMode, saving, setSaving, errors, setErrors, completion }) => {
  const validatePhone = (v) => {
    if (!v) return "Phone number is required.";
    if (!/^\d+$/.test(v)) return "Only numbers allowed.";
    if (v.length !== 10) return "Must be 10 digits.";
    return "";
  };

  const handleSave = async () => {
    const phoneErr = validatePhone(user.phoneNumber);
    if (phoneErr) { setErrors({ phoneNumber: phoneErr }); return; }
    try {
      setSaving(true);
      const token = localStorage.getItem("auth-token");
      const res = await fetch(`${backend_url}/saveuserdetails`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "auth-token": token },
        body: JSON.stringify(user),
      });
      const data = await res.json();
      if (data.success) {
        setEditMode(false);
        toast.success("Profile saved!");
      }
    } catch { toast.error("Save failed."); }
    finally { setSaving(false); }
  };

  const fieldDefs = [
    { label: "Full Name", key: "name", icon: <FaUser />, readonly: true },
    { label: "Email", key: "email", icon: <FaEnvelope />, readonly: true },
    { label: "Phone Number", key: "phoneNumber", icon: <FaPhone />, type: "tel",
      onChange: (e) => {
        const v = e.target.value.replace(/\D/g, "");
        if (v.length <= 10) { setUser({ ...user, phoneNumber: v }); setErrors({ phoneNumber: validatePhone(v) }); }
      }
    },
    { label: "Gender", key: "gender", icon: <FaVenusMars />, isSelect: true,
      options: ["Male", "Female", "Other", "Prefer not to say"] },
    { label: "Address", key: "address", icon: <FaMapMarkerAlt /> },
    { label: "City", key: "city", icon: <FaCity /> },
    { label: "Pincode", key: "pincode", icon: <MdLocationOn />,
      onChange: (e) => setUser({ ...user, pincode: e.target.value.replace(/\D/g, "").slice(0, 6) }) },
    { label: "Country", key: "country", icon: <FaGlobe />, isSelect: true,
      options: ["India", "Other"],
      onChange: (e) => setUser({ ...user, country: e.target.value, state: "" }) },
  ];

  if (user.country === "India") {
    fieldDefs.push({ label: "State", key: "state", icon: <FaMapMarkerAlt />,
      isSelect: true, options: indianStates });
  }

  return (
    <div className="space-y-4">
      {/* Incomplete banner */}
      {completion < 100 && !editMode && (
        <div onClick={() => setEditMode(true)}
          className="flex items-center gap-3 p-4 rounded-2xl border border-yellow-200 cursor-pointer hover:bg-yellow-50 transition"
          style={{ background: "#fffbeb" }}>
          <span className="text-2xl">✏️</span>
          <div className="flex-1">
            <p className="text-sm font-bold text-yellow-800">Complete your profile — {completion}% done</p>
            <p className="text-xs text-yellow-600 mt-0.5">Fill in your details to auto-fill orders faster. Click to edit.</p>
          </div>
          <div className="text-yellow-500">
            <FaEdit />
          </div>
        </div>
      )}

      {/* Profile card */}
      <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--brand-primary-50)]"
          style={{ background: "linear-gradient(135deg,var(--brand-primary-50),var(--brand-primary-50))" }}>
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg flex items-center justify-center"
              style={{ background: "linear-gradient(135deg,var(--brand-primary-500),var(--brand-primary-400))" }}>
              <FaUser className="text-white text-xs" />
            </div>
            <h2 className="text-base font-bold text-gray-800">Profile Details</h2>
          </div>
          {editMode ? (
            <button onClick={handleSave} disabled={saving}
              className="flex items-center gap-2 text-sm font-bold px-4 py-2 rounded-xl text-white shadow transition hover:opacity-90"
              style={{ background: "linear-gradient(135deg,#059669,#10b981)" }}>
              <FaSave size={12} />{saving ? "Saving…" : "Save Changes"}
            </button>
          ) : (
            <button onClick={() => setEditMode(true)}
              className="flex items-center gap-2 text-sm font-bold px-4 py-2 rounded-xl text-white shadow transition hover:opacity-90"
              style={{ background: "linear-gradient(135deg,var(--brand-primary-500),var(--brand-primary-400))" }}>
              <FaEdit size={12} /> Edit
            </button>
          )}
        </div>

        {/* Fields grid */}
        <div className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
          {fieldDefs.map(({ label, key, icon, readonly, type = "text", isSelect, options, onChange }) => {
            const val = user[key];
            const isEmpty = !val || val === "";
            return (
              <div key={key} className={`rounded-xl p-3.5 border transition-all ${
                isEmpty && !editMode ? "border-orange-200 bg-orange-50/40" : "border-gray-100 bg-gray-50"}`}>
                <div className="flex items-center gap-2 mb-1.5">
                  <span className="text-[var(--brand-primary-400)] text-xs">{icon}</span>
                  <label className="text-xs font-bold text-gray-500 uppercase tracking-wide">{label}</label>
                  {isEmpty && !editMode && <span className="ml-auto text-[10px] text-orange-500 font-semibold bg-orange-100 px-2 py-0.5 rounded-full">Empty</span>}
                </div>
                {editMode && !readonly ? (
                  isSelect ? (
                    <select value={val || ""} onChange={onChange || ((e) => setUser({ ...user, [key]: e.target.value }))}
                      className="w-full text-sm bg-white border border-[var(--brand-primary-200)] rounded-lg px-3 py-2 focus:ring-2 focus:ring-[var(--brand-primary-400)] outline-none">
                      <option value="">Select {label}</option>
                      {options.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  ) : (
                    <input type={type} value={val || ""}
                      onChange={onChange || ((e) => setUser({ ...user, [key]: e.target.value }))}
                      className={`w-full text-sm bg-white border rounded-lg px-3 py-2 focus:ring-2 focus:ring-[var(--brand-primary-400)] outline-none ${errors[key] ? "border-red-400" : "border-[var(--brand-primary-200)]"}`} />
                  )
                ) : (
                  <p className={`text-sm font-semibold ${isEmpty ? "text-orange-400 italic" : "text-gray-800"}`}>
                    {isEmpty ? "Not set" : val}
                  </p>
                )}
                {errors[key] && <p className="text-red-500 text-xs mt-1">{errors[key]}</p>}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// ──────────────────────── TRACK ORDER ────────────────────────
export const TrackOrderSection = ({ initialId = "" }) => {
  // Allow deep-linking from the confirmation email: /track?orderId=... or ?tab=orders&orderId=...
  const urlId = initialId || new URLSearchParams(window.location.search).get("orderId") || "";
  const [orderId, setOrderId] = useState(urlId);
  const [order, setOrder] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  // When arriving via a track link the ID is already known, so hide the manual search.
  const [showSearch, setShowSearch] = useState(!urlId);

  const trackById = async (id) => {
    const trimmed = (id || "").trim();
    if (!trimmed) { setError("Please enter your order number"); return; }
    setLoading(true); setOrder(null); setError("");
    try {
      const res = await fetch(`${backend_url}/track-order/${trimmed}`);
      const data = await res.json();
      if (data.success) setOrder(data.order);
      else setError("Order not found. Please check the order number and try again.");
    } catch { setError("Something went wrong."); }
    finally { setLoading(false); }
  };

  const handleTrackOrder = () => trackById(orderId);

  // Auto-track when an Order ID is supplied via the URL (click-through from email).
  useEffect(() => {
    if (urlId) trackById(urlId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-5 sm:p-6">
      {/* Section header */}
      <div className="flex items-center gap-3 mb-5 pb-4 border-b border-gray-200">
        <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: "var(--brand-primary-600)" }}>
          <FaShippingFast className="text-white text-sm" />
        </div>
        <div>
          <h2 className="text-base font-bold text-gray-800 leading-tight">Track Your Order</h2>
          <p className="text-xs text-gray-400">Enter your order number (e.g. 1234-5678-9012) to see the latest status</p>
        </div>
      </div>

      {/* Search — shown for manual entry; hidden when the order auto-loaded from a link */}
      {showSearch ? (
        <div className="flex flex-col sm:flex-row gap-2.5">
          <input type="text" value={orderId} onChange={(e) => setOrderId(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleTrackOrder()}
            placeholder="e.g. 1234-5678-9012"
            inputMode="numeric" autoComplete="off"
            className="flex-1 border border-gray-200 p-3 rounded-lg text-base sm:text-sm focus:ring-2 focus:ring-[var(--brand-primary-500)]/30 focus:border-[var(--brand-primary-400)] outline-none transition-all" />
          <button onClick={handleTrackOrder} disabled={loading}
            className="text-white px-6 py-3 rounded-lg text-sm font-semibold transition-colors hover:brightness-110 active:scale-[0.99] disabled:opacity-60 flex items-center justify-center gap-2 whitespace-nowrap"
            style={{ background: "var(--brand-primary-600)" }}>
            {loading ? (<><span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span> Tracking…</>) : "Track Order"}
          </button>
        </div>
      ) : (
        <button onClick={() => { setShowSearch(true); setOrderId(""); setOrder(null); setError(""); }}
          className="text-sm font-semibold text-[var(--brand-primary-600)] hover:text-[var(--brand-primary-800)] transition-colors">
          Track a different order
        </button>
      )}

      {error && (
        <div className="mt-4 flex items-center gap-2 text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2.5 text-sm font-medium">
          <FaTimesCircle className="flex-shrink-0" /> {error}
        </div>
      )}

      {order && (() => {
        const isCancelled = order.status === "Cancelled";
        const currentIndex = STATUS_INDEX[order.status] ?? 0;
        const estDate = order.date ? new Date(new Date(order.date).getTime() + 5 * 864e5) : null;
        const estDelivery = estDate
          ? estDate.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })
          : "N/A";

        // Days-to-delivery calculation
        const daysLeft = estDate ? Math.ceil((estDate.getTime() - Date.now()) / 864e5) : null;
        const deliveryText =
          order.status === "Delivered" ? "Your order has been delivered."
            : daysLeft === null ? `Estimated delivery by ${estDelivery}`
            : daysLeft > 1 ? `Arriving in ${daysLeft} days · by ${estDelivery}`
            : daysLeft === 1 ? `Arriving tomorrow · by ${estDelivery}`
            : daysLeft === 0 ? `Arriving today · by ${estDelivery}`
            : `Arriving soon · expected by ${estDelivery}`;

        const banner = isCancelled
          ? { Icon: FaTimesCircle, label: "Order Cancelled", sub: "This order has been cancelled.", fg: "#B91C1C", iconBg: "linear-gradient(135deg,#EF4444,#DC2626)", grad: "linear-gradient(120deg,#FEF2F2,#FFFFFF)", border: "#FECACA" }
          : order.status === "Delivered"
          ? { Icon: FaCheckCircle, label: "Delivered", sub: deliveryText, fg: "#15803D", iconBg: "linear-gradient(135deg,#22C55E,#16A34A)", grad: "linear-gradient(120deg,#F0FDF4,#FFFFFF)", border: "#BBF7D0" }
          : { Icon: statusIcons[STATUS_FLOW[currentIndex]], label: `Order ${STATUS_FLOW[currentIndex]}`, sub: deliveryText, fg: "var(--brand-primary-700)", iconBg: "linear-gradient(135deg,var(--brand-primary-500),var(--brand-primary-400))", grad: "linear-gradient(120deg,var(--brand-primary-50),#FFFFFF)", border: "var(--brand-primary-200)" };
        const BannerIcon = banner.Icon;

        const showCountdown = !isCancelled && order.status !== "Delivered" && daysLeft !== null && daysLeft >= 0;
        const waMsg = encodeURIComponent(`Hi, I need help with my order ${order.orderNumber || order._id} (${order.product?.productName || "my order"}).`);

        return (
          <div className="mt-6 space-y-4">
            {/* Current status banner */}
            <div className="relative overflow-hidden flex items-center gap-4 rounded-2xl border p-4" style={{ borderColor: banner.border, background: banner.grad }}>
              <div className="w-11 h-11 rounded-full flex items-center justify-center text-white flex-shrink-0 shadow-md" style={{ background: banner.iconBg }}>
                <BannerIcon className="text-lg" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="font-bold text-sm" style={{ color: banner.fg }}>{banner.label}</p>
                <p className="text-xs text-gray-500">{banner.sub}</p>
              </div>
              {!isCancelled && (
                <span className="hidden sm:inline-flex items-center text-[11px] font-bold px-3 py-1 rounded-full text-white flex-shrink-0 shadow-sm" style={{ background: banner.iconBg }}>
                  {order.status === "Delivered" ? "Completed" : `Step ${currentIndex + 1} of ${STATUS_FLOW.length}`}
                </span>
              )}
            </div>

            {/* Product + amount + days */}
            <div className="flex flex-col sm:flex-row items-stretch gap-3">
              {/* Product card */}
              <div className="flex items-center gap-4 bg-white rounded-2xl border border-gray-200 p-4 flex-1 min-w-0">
                <img
                  crossOrigin="anonymous"
                  src={order.product?.image || ""}
                  alt={order.product?.productName || "Product"}
                  className="w-20 h-20 rounded-xl border border-gray-200 object-cover flex-shrink-0 bg-gray-50"
                />
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-gray-900 text-sm sm:text-base leading-snug line-clamp-2">
                    {order.product?.productName || "Your order"}
                  </p>
                  <span className="inline-block mt-1.5 text-[11px] font-medium text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
                    Qty {order.product?.quantity ?? 1}
                  </span>
                  <p className="font-mono text-[11px] text-gray-400 mt-1.5">#{order.orderNumber || order._id.slice(-7).toUpperCase()}</p>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-[11px] text-gray-400 font-semibold uppercase tracking-wide">Amount</p>
                  <p className="text-lg font-black" style={{ color: "var(--brand-primary-600)" }}>₹{Number(order.totalPrice).toFixed(2)}</p>
                </div>
              </div>

              {/* Days-to-delivery countdown */}
              {showCountdown && (
                <div className="flex sm:flex-col items-center justify-center gap-3 sm:gap-1 rounded-2xl border border-[var(--brand-primary-100)] px-5 py-4 sm:w-40 flex-shrink-0" style={{ background: "linear-gradient(135deg,var(--brand-primary-50),#FFFFFF)" }}>
                  <span className="text-3xl font-black leading-none" style={{ color: "var(--brand-primary-600)" }}>{daysLeft}</span>
                  <div className="text-center">
                    <p className="text-xs font-bold text-gray-700 leading-tight">{daysLeft === 1 ? "day to go" : "days to go"}</p>
                    <p className="text-[10px] text-gray-400">by {estDelivery}</p>
                  </div>
                </div>
              )}
            </div>

            {/* Need help — contact admin on WhatsApp */}
            <a
              href={`https://wa.me/919789389489?text=${waMsg}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 w-full rounded-xl border border-green-200 bg-green-50 text-green-700 py-2.5 text-sm font-semibold hover:bg-green-100 transition-colors"
            >
              <FaWhatsapp className="text-base" /> Need help with this order? Contact us on WhatsApp
            </a>

            {/* Timeline */}
            {isCancelled ? (
              <div className="flex items-center justify-center gap-2 text-sm text-gray-400 py-6 border border-dashed border-gray-200 rounded-xl">
                <FaRegClock /> Tracking is unavailable for cancelled orders.
              </div>
            ) : (
              <div className="rounded-2xl border border-gray-100 bg-gray-50/50 p-5 sm:p-6">
                <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-5">Order Progress</p>
                <div>
                  {STATUS_FLOW.map((step, idx) => {
                    const done = idx < currentIndex;
                    const isActive = idx === currentIndex;
                    const reached = done || isActive;
                    const isLast = idx === STATUS_FLOW.length - 1;
                    const StepIcon = statusIcons[step];
                    return (
                      <div key={step} className="flex gap-4 last:pb-0 pb-1">
                        {/* Node + rail column */}
                        <div className="flex flex-col items-center flex-shrink-0">
                          <div className={`w-11 h-11 rounded-full flex items-center justify-center transition-all duration-500
                            ${done ? "text-white" : isActive ? "bg-white border-2 ring-4 ring-[var(--brand-primary-100)]" : "bg-white border-2 border-gray-200 text-gray-300"}`}
                            style={done
                              ? { background: "linear-gradient(135deg,var(--brand-primary-500),var(--brand-primary-400))", boxShadow: "0 6px 14px rgba(109,59,181,0.30)" }
                              : isActive
                              ? { borderColor: "var(--brand-primary-600)", color: "var(--brand-primary-600)", boxShadow: "0 4px 12px rgba(109,59,181,0.20)" }
                              : undefined}>
                            {done ? <FaCheck className="text-base" /> : <StepIcon className="text-lg" />}
                          </div>
                          {!isLast && (
                            <div className="w-[3px] flex-1 min-h-[28px] my-1 rounded-full" style={{ background: done ? "linear-gradient(180deg,var(--brand-primary-500),var(--brand-primary-400))" : "#E5E7EB" }} />
                          )}
                        </div>

                        {/* Content */}
                        <div className={`min-w-0 pt-1.5 ${isLast ? "" : "pb-4"}`}>
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className={`font-bold text-sm ${reached ? "text-gray-900" : "text-gray-400"}`}>{step}</p>
                            {isActive && (
                              <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full text-white" style={{ background: "var(--brand-primary-600)" }}>
                                <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" /> In progress
                              </span>
                            )}
                            {done && <FaCheckCircle className="text-green-500 text-sm" />}
                          </div>
                          <p className={`text-xs mt-0.5 ${reached ? "text-gray-500" : "text-gray-400"}`}>{STEP_DESC[step]}</p>
                          {isActive && (
                            <p className="text-[11px] font-semibold mt-1.5" style={{ color: "var(--brand-primary-600)" }}>
                              Estimated delivery by {estDelivery}
                            </p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        );
      })()}
    </div>
  );
};

// ──────────────────────── ORDERS TABLE ────────────────────────
const OrdersTable = ({ user, backend_url }) => {
  const [myOrders, setMyOrders] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const ordersPerPage = 5;

  useEffect(() => {
    if (!user?.userId) return;
    const fetchOrders = async () => {
      try {
        const res = await fetch(`${backend_url}/orders/user/${user.userId}`, {
          headers: { "auth-token": localStorage.getItem("auth-token") || "" },
        });
        const data = await res.json();
        if (data.success) setMyOrders(data.orders || []);
      } catch { console.error("Error fetching orders"); }
    };
    fetchOrders();
  }, [backend_url, user?.userId]);

  const getDeliveryDate = (d) => {
    if (!d) return "N/A";
    const date = new Date(d);
    date.setDate(date.getDate() + 5);
    return date.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  };

  const statusMeta = (s) => ({
    Pending:            { pill: "bg-amber-50 text-amber-700 border-amber-200",   dot: "#F59E0B" },
    Confirmed:          { pill: "bg-blue-50 text-blue-700 border-blue-200",       dot: "#3B82F6" },
    Packed:             { pill: "bg-[var(--brand-primary-50)] text-[var(--brand-primary-700)] border-[var(--brand-primary-200)]", dot: "var(--brand-primary-500)" },
    Shipped:            { pill: "bg-[var(--brand-secondary-50)] text-[var(--brand-secondary-700)] border-[var(--brand-secondary-200)]", dot: "var(--brand-secondary-500)" },
    "Out for Delivery": { pill: "bg-orange-50 text-orange-700 border-orange-200", dot: "#F97316" },
    Delivered:          { pill: "bg-green-50 text-green-700 border-green-200",     dot: "#22C55E" },
    Cancelled:          { pill: "bg-red-50 text-red-700 border-red-200",           dot: "#EF4444" },
  }[s] || { pill: "bg-gray-50 text-gray-600 border-gray-200", dot: "#9CA3AF" });

  const estDeliveryLabel = (order) =>
    order.status === "Delivered" ? "Delivered"
      : order.status === "Cancelled" ? "—"
      : getDeliveryDate(order.orderDate);

  const indexOfLastOrder = currentPage * ordersPerPage;
  const currentOrders = myOrders.slice(indexOfLastOrder - ordersPerPage, indexOfLastOrder);
  const totalPages = Math.ceil(myOrders.length / ordersPerPage);

  return (
    <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="flex items-center gap-3 px-5 sm:px-6 py-4 border-b border-gray-200">
        <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: "var(--brand-primary-600)" }}>
          <FaShoppingBag className="text-white text-sm" />
        </div>
        <div>
          <h2 className="text-base font-bold text-gray-800 leading-tight">My Orders</h2>
          <p className="text-xs text-gray-400">Track and manage your recent purchases</p>
        </div>
        <span className="ml-auto text-xs font-semibold px-3 py-1 rounded-full bg-[var(--brand-primary-50)] text-[var(--brand-primary-700)] border border-[var(--brand-primary-100)]">
          {myOrders.length} {myOrders.length === 1 ? "order" : "orders"}
        </span>
      </div>

      {myOrders.length === 0 ? (
        <div className="text-center py-16 px-4">
          <FaShoppingBag className="text-5xl mx-auto mb-3 text-gray-200" />
          <p className="font-semibold text-gray-500">No orders yet</p>
          <p className="text-xs text-gray-400 mt-1">Your placed orders will appear here.</p>
        </div>
      ) : (
        <>
          {/* Desktop table */}
          <div className="hidden md:block overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  {["Order ID","Product","Price","Qty","Est. Delivery","Status"].map((h) => (
                    <th key={h} className="py-3 px-4 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wide">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {currentOrders.map((order) => {
                  const meta = statusMeta(order.status);
                  return (
                    <tr key={order._id} className="border-b border-gray-100 last:border-0 hover:bg-gray-50/70 transition-colors">
                      <td className="py-3 px-4 font-mono text-xs text-gray-500">#{order.orderNumber || order._id.slice(-7).toUpperCase()}</td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-3">
                          <img crossOrigin="anonymous"
                            src={order.productId?.images?.[0] || ""}
                            alt={order.productId?.name || "Product"}
                            className="w-12 h-12 object-cover rounded-lg border border-gray-200 flex-shrink-0" />
                          <span className="text-gray-900 font-medium line-clamp-2 max-w-[180px]">{order.productId?.name || "Unknown"}</span>
                        </div>
                      </td>
                      <td className="py-3 px-4 font-semibold text-gray-900 whitespace-nowrap">₹{Number(order.pricePerUnit).toFixed(2)}</td>
                      <td className="py-3 px-4 text-gray-600">×{order.quantity}</td>
                      <td className="py-3 px-4 text-gray-500 text-xs whitespace-nowrap">{estDeliveryLabel(order)}</td>
                      <td className="py-3 px-4">
                        <div className="flex flex-col gap-1.5 items-start">
                          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${meta.pill}`}>
                            <span className="w-1.5 h-1.5 rounded-full" style={{ background: meta.dot }} />
                            {order.status}
                          </span>
                          <div className="flex items-center gap-2">
                            <Link to={`/track?orderId=${order._id}`}
                              className="text-xs font-semibold text-[var(--brand-primary-600)] hover:text-[var(--brand-primary-800)] transition-colors">Track</Link>
                            <span className="text-gray-200">|</span>
                            <a href={`https://wa.me/919789389489?text=${encodeURIComponent(`Hi, I need help with my order ${order.orderNumber || order._id}.`)}`}
                              target="_blank" rel="noopener noreferrer"
                              className="text-xs font-medium text-green-600 hover:text-green-700 transition-colors inline-flex items-center gap-1">
                              <FaWhatsapp /> Help
                            </a>
                          </div>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="md:hidden divide-y divide-gray-100">
            {currentOrders.map((order) => {
              const meta = statusMeta(order.status);
              return (
                <div key={order._id} className="p-4">
                  <div className="flex gap-3">
                    <img crossOrigin="anonymous"
                      src={order.productId?.images?.[0] || ""}
                      alt={order.productId?.name || "Product"}
                      className="w-16 h-16 object-cover rounded-lg border border-gray-200 flex-shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-start justify-between gap-2">
                        <p className="text-gray-900 font-semibold text-sm line-clamp-2">{order.productId?.name || "Unknown"}</p>
                        <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-semibold border flex-shrink-0 ${meta.pill}`}>
                          <span className="w-1.5 h-1.5 rounded-full" style={{ background: meta.dot }} />
                          {order.status}
                        </span>
                      </div>
                      <p className="font-mono text-[11px] text-gray-400 mt-1">#{order.orderNumber || order._id.slice(-7).toUpperCase()}</p>
                      <div className="flex items-center gap-3 mt-2 text-xs text-gray-500">
                        <span className="font-semibold text-gray-900">₹{Number(order.pricePerUnit).toFixed(2)}</span>
                        <span>Qty ×{order.quantity}</span>
                        <span className="ml-auto">{estDeliveryLabel(order)}</span>
                      </div>
                      <div className="flex items-center gap-3 mt-2">
                        <Link to={`/track?orderId=${order._id}`}
                          className="text-xs font-semibold text-[var(--brand-primary-600)] hover:text-[var(--brand-primary-800)] transition-colors">Track order</Link>
                        <a href={`https://wa.me/919789389489?text=${encodeURIComponent(`Hi, I need help with my order ${order.orderNumber || order._id}.`)}`}
                          target="_blank" rel="noopener noreferrer"
                          className="text-xs font-medium text-green-600 hover:text-green-700 transition-colors inline-flex items-center gap-1">
                          <FaWhatsapp /> Help
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-center items-center gap-2 py-4 border-t border-gray-100">
              <button onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))} disabled={currentPage === 1}
                className="px-3 py-1.5 rounded-lg text-xs font-semibold border border-gray-200 text-gray-600 hover:border-[var(--brand-primary-300)] hover:text-[var(--brand-primary-700)] disabled:opacity-40 transition-colors">Prev</button>
              {Array.from({ length: totalPages }, (_, i) => (
                <button key={i} onClick={() => setCurrentPage(i + 1)}
                  className={`w-8 h-8 rounded-lg text-xs font-bold transition-colors ${currentPage === i + 1 ? "text-white" : "border border-gray-200 text-gray-600 hover:border-[var(--brand-primary-300)]"}`}
                  style={currentPage === i + 1 ? { background: "var(--brand-primary-600)" } : {}}>{i + 1}</button>
              ))}
              <button onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))} disabled={currentPage === totalPages}
                className="px-3 py-1.5 rounded-lg text-xs font-semibold border border-gray-200 text-gray-600 hover:border-[var(--brand-primary-300)] hover:text-[var(--brand-primary-700)] disabled:opacity-40 transition-colors">Next</button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default UserDetails;
